import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
  type RefObject,
} from "react"
import Image from "next/image"
import { usePathname } from "next/navigation"
import { MEDIA_QUERIES } from "@/constants/ui"
import { useMediaQuery } from "@/hooks/use-media-query"
import { cn } from "cn"
import Lottie, { LottieRefCurrentProps } from "lottie-react"
import { useInView } from "react-intersection-observer"

import { CF_S3_URL } from "@/lib/cloudflare-loader"
import { AriaButton } from "@/components/ui/aria-button"
import { Button } from "@/components/ui/button"

export type ScrollerSection = {
  title: string
  description: ReactNode
}

type BusinessScrollerProps = {
  sections: ScrollerSection[]
  lottieFilePattern: (index: number) => string
  gradientBackground?: string
}

function MobileScrollerSection({
  animationData,
}: {
  animationData: object | null
}) {
  const lottieRef = useRef<LottieRefCurrentProps>(null)
  const { ref: sectionRef, inView: sectionInView } = useInView({
    threshold: 0.3,
  })

  useEffect(() => {
    if (lottieRef.current) {
      if (sectionInView) {
        lottieRef.current.play()
      } else {
        lottieRef.current.stop()
      }
    }
  }, [sectionInView])

  if (!animationData) return null

  return (
    <div ref={sectionRef}>
      <Lottie
        lottieRef={lottieRef}
        animationData={animationData}
        loop={false}
        autoplay={false}
        className="relative mx-auto aspect-450/626 max-w-[500px]"
      />
    </div>
  )
}

export function BusinessScroller({
  sections,
  lottieFilePattern,
  gradientBackground,
}: BusinessScrollerProps) {
  const pathname = usePathname()
  const isLg = useMediaQuery(MEDIA_QUERIES.lg)
  const [activeSectionIndex, setActiveSectionIndex] = useState(0)
  const [scrollProgress, setScrollProgress] = useState(0)
  const [sectionHeight, setSectionHeight] = useState(0)
  const containerRef = useRef<HTMLDivElement>(null)
  const videoWrapperRef = useRef<HTMLDivElement>(null)
  const lottieRefs = useRef<RefObject<LottieRefCurrentProps | null>[]>(
    sections.map(() => ({ current: null }))
  )
  const [lottieData, setLottieData] = useState<(object | null)[]>(
    Array(sections.length).fill(null)
  )

  const { ref: inViewRef, inView } = useInView()

  const setRefs = useCallback(
    (node: HTMLDivElement | null) => {
      ;(containerRef as RefObject<HTMLDivElement | null>).current = node
      inViewRef(node)
    },
    [inViewRef]
  )

  useEffect(() => {
    const updateSectionHeight = () => {
      const viewportHeight = window.innerHeight
      const availableHeight = viewportHeight - 128

      const minHeight = videoWrapperRef.current?.clientHeight || 0
      const newHeight = Math.max(availableHeight, minHeight)

      setSectionHeight(newHeight)
    }

    updateSectionHeight()
    window.addEventListener("resize", updateSectionHeight)

    return () => window.removeEventListener("resize", updateSectionHeight)
  }, [])

  useEffect(() => {
    const loadLottie = async (index: number) => {
      try {
        const response = await fetch(`/lottie/${lottieFilePattern(index)}.json`)
        const data = await response.json()
        setLottieData((prev) => {
          const newData = [...prev]
          newData[index] = data
          return newData
        })
      } catch (error) {
        console.error(`Failed to load Lottie animation ${index + 1}:`, error)
      }
    }

    if (!lottieData[0]) {
      void loadLottie(0)
    }

    if (!isLg || inView) {
      sections.forEach((_, i) => {
        if (i > 0 && !lottieData[i]) {
          void loadLottie(i)
        }
      })
    }
  }, [inView, lottieData, isLg, lottieFilePattern, sections])

  useEffect(() => {
    lottieRefs.current.forEach((lottieRef, i) => {
      const lottie = lottieRef.current
      if (lottie) {
        if (inView && activeSectionIndex === i) {
          lottie.play()
        } else {
          lottie.stop()
        }
      }
    })
  }, [activeSectionIndex, inView])

  const scrollToSection = (sectionIndex: number) => {
    if (!containerRef.current) return

    const containerRect = containerRef.current.getBoundingClientRect()
    const containerTop = containerRect.top + window.scrollY
    const containerHeight = containerRect.height
    const viewportHeight = window.innerHeight
    const maxScroll = containerHeight - viewportHeight

    const targetProgress = sectionIndex / (sections.length - 1)
    const targetScroll = containerTop + targetProgress * maxScroll

    window.scrollTo({
      top: targetScroll,
      behavior: "smooth",
    })
  }

  useEffect(() => {
    if (!inView) return

    const handleScroll = () => {
      if (!containerRef.current) return

      const containerRect = containerRef.current.getBoundingClientRect()
      const containerTop = containerRect.top
      const containerHeight = containerRect.height
      const viewportHeight = window.innerHeight

      const scrolled = Math.max(0, -containerTop)
      const maxScroll = containerHeight - viewportHeight
      const progress = Math.min(scrolled / maxScroll, 1)

      setScrollProgress(progress)

      const sectionProgress = progress * (sections.length - 1)
      const activeIndex = Math.round(sectionProgress)
      setActiveSectionIndex(
        Math.min(Math.max(activeIndex, 0), sections.length - 1)
      )
    }

    window.addEventListener("scroll", handleScroll)
    handleScroll()

    return () => window.removeEventListener("scroll", handleScroll)
  }, [inView, sections.length])

  const renderInfo = (section: ScrollerSection) => (
    <div className="px-5 lg:px-0">
      <h3 className="mb-6 font-circular text-3xl text-balance sm:text-5xl sm:font-semibold">
        {section.title}
      </h3>
      <div className="prose text-md leading-relaxed text-balance">
        {section.description}
      </div>
      <div className="mt-8 flex justify-center gap-1.5 lg:justify-start">
        <Button
          variant="outline-clear"
          size={isLg ? "xl" : "lg"}
          className="border-2 hover:bg-white"
          onClick={(e) => {
            e.preventDefault()
            document.getElementById("contact")?.scrollIntoView({
              behavior: "smooth",
            })
          }}
        >
          Book a demo
        </Button>
        {pathname !== "/business/how-it-works" && (
          <Button
            variant="ghost"
            size={isLg ? "xl" : "lg"}
            href="/business/how-it-works"
          >
            How it works
          </Button>
        )}
      </div>
    </div>
  )

  if (isLg) {
    return (
      <div
        ref={setRefs}
        className="relative z-0"
        style={{ height: `${sectionHeight * sections.length}px` }}
      >
        <div
          className={cn(
            "sticky top-16",
            !!gradientBackground &&
              "overflow-hidden rounded-[4rem] bg-linear-to-br",
            gradientBackground
          )}
          style={{ height: `${sectionHeight}px` }}
        >
          {!!gradientBackground && (
            <>
              <Image
                src={`${CF_S3_URL}/web/ios/cloud-left.png`}
                alt=""
                width={842}
                height={711}
                className="pointer-events-none absolute top-1/2 left-[-7%] z-1 w-3/5 -translate-y-1/2"
              />

              <Image
                src={`${CF_S3_URL}/web/ios/cloud-right.png`}
                alt=""
                width={655}
                height={711}
                className="pointer-events-none absolute top-[52%] right-0 z-1 w-3/5"
              />
            </>
          )}

          <div className="absolute top-1/2 left-0 z-1 w-1/2 -translate-y-1/2 xl:left-[17.7%] xl:w-[36.5%]">
            <div className="mx-auto max-w-[500px]">
              <div className="aspect-500/696" ref={videoWrapperRef}>
                {sections.map((section, i) => (
                  <div
                    key={section.title}
                    className={cn(
                      "absolute inset-0 size-full transition-opacity duration-300",
                      activeSectionIndex === i ? "opacity-100" : "opacity-0"
                    )}
                  >
                    {lottieData[i] && (
                      <Lottie
                        lottieRef={lottieRefs.current[i]}
                        animationData={lottieData[i]}
                        loop={false}
                        autoplay={false}
                        className="size-full"
                      />
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div
            className="absolute inset-0"
            style={{
              transform: `translateY(${-scrollProgress * (sections.length - 1) * sectionHeight}px)`,
            }}
          >
            {sections.map((section, i) => {
              const sectionProgress = scrollProgress * (sections.length - 1)
              const distanceFromCenter = Math.abs(sectionProgress - i)
              const opacity = Math.max(0, 1 - distanceFromCenter)

              return (
                <div
                  key={section.title}
                  id={section.title}
                  className="absolute inset-0"
                  style={
                    {
                      height: `${sectionHeight}px`,
                      transform: `translateY(${i * sectionHeight}px)`,
                    } as CSSProperties
                  }
                >
                  <div
                    className="absolute top-1/2 right-0 left-[54%] -translate-y-1/2 xl:left-[60%]"
                    style={{ opacity }}
                  >
                    <div className="max-w-96">{renderInfo(section)}</div>
                  </div>
                </div>
              )
            })}
          </div>

          <div className="absolute inset-y-0 left-[8.9%] z-1 hidden flex-col justify-center gap-6 xl:flex">
            {sections.map((section, i) => (
              <AriaButton
                key={section.title}
                onClick={() => scrollToSection(i)}
                className={cn(
                  "size-4 rounded-full bg-black",
                  i !== activeSectionIndex && "opacity-20"
                )}
              />
            ))}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-5 text-center">
      {sections.map((section, i) => (
        <div
          key={section.title}
          className={cn("rounded-4xl pt-10", gradientBackground)}
        >
          {renderInfo(section)}
          <MobileScrollerSection animationData={lottieData[i]} />
        </div>
      ))}
    </div>
  )
}
